import type { ColumnsType } from 'antd/lib/table';
import moment from 'moment';

import {
  ConsentSignalIcon,
  consentStatusLabel,
} from '../../../../components/common/consentSignal/consentSignalStatus';
import {
  StopoverDataItemType,
  StopoverPendencyRowType,
  StopoverPendencySituationType,
} from '../../../../types';
import { createDateStringPtBr } from '../../../../utils/utils';
import { StopoverDataCheckCell } from './stopoverDataCheckCell';

const SITUATION_LABELS: Record<StopoverPendencySituationType, string> = {
  EXPECTED: 'Esperado',
  ANCHORED: 'Fundeado',
  DOCKED: 'Atracado',
  REDOCKABLE: 'Reatracável',
  UNDOCKED: 'Desatracado',
  CANCELED: 'Cancelado',
};

function text(value: string | number | null | undefined) {
  return value === null || value === undefined || value === '' ? '-' : value;
}

export type ExemptionHandlers = {
  canEdit: boolean;
  savingKey: string | null;
  onExempt: (row: StopoverPendencyRowType, item: StopoverDataItemType) => void;
  onRemoveExemption: (
    row: StopoverPendencyRowType,
    item: StopoverDataItemType
  ) => void;
};

export function exemptionKey(
  row: StopoverPendencyRowType,
  item: StopoverDataItemType
) {
  return `${row.id}-${row.docking_id}-${item}`;
}

// Mesmo formato da planilha "Check-List - Programação Suape": Conferência,
// identificação, DUV, Situação, os demais itens (P/A/I) e, depois, anuências.
export function stopoverPendencyColumns(
  checkColumns: { key: StopoverDataItemType; label: string }[],
  consentColumns: { key: string; label: string }[],
  handlers: ExemptionHandlers
): ColumnsType<StopoverPendencyRowType> {
  const checkColumn = (column: {
    key: StopoverDataItemType;
    label: string;
  }) => ({
    title: column.label,
    key: `check_${column.key}`,
    width: 90,
    align: 'center' as const,
    render: (_: unknown, row: StopoverPendencyRowType) => (
      <StopoverDataCheckCell
        item={column.key}
        label={column.label}
        check={row.checks[column.key]}
        canEdit={handlers.canEdit}
        isSaving={handlers.savingKey === exemptionKey(row, column.key)}
        onExempt={() => handlers.onExempt(row, column.key)}
        onRemoveExemption={() => handlers.onRemoveExemption(row, column.key)}
      />
    ),
  });
  const duvColumn = checkColumns.filter((column) => column.key === 'DUV');
  const otherChecks = checkColumns.filter((column) => column.key !== 'DUV');

  return [
    {
      title: 'Conferência',
      key: 'conference',
      fixed: 'left',
      width: 130,
      render: (_, row) =>
        row.has_data_pendency ? (
          <span style={{ color: '#D46B08', fontWeight: 600 }}>⚠️ PENDENTE</span>
        ) : (
          <span style={{ color: '#389E0D', fontWeight: 600 }}>✅ OK</span>
        ),
    },
    {
      title: 'Navio',
      dataIndex: 'vessel_name',
      key: 'vessel_name',
      fixed: 'left',
      width: 180,
    },
    {
      title: 'ETA',
      dataIndex: 'expected_arrival',
      key: 'expected_arrival',
      width: 140,
      render: (value) => text(createDateStringPtBr(value)),
    },
    {
      title: 'VG',
      dataIndex: 'shipowner_trip',
      key: 'shipowner_trip',
      width: 100,
      render: text,
    },
    {
      title: 'Terminal',
      dataIndex: 'docking_place_name',
      key: 'docking_place_name',
      width: 120,
      render: text,
    },
    ...duvColumn.map(checkColumn),
    {
      title: 'Situação',
      dataIndex: 'situation',
      key: 'situation',
      width: 120,
      render: (value: StopoverPendencySituationType) =>
        SITUATION_LABELS[value] || text(value),
    },
    ...otherChecks.map(checkColumn),
    ...consentColumns.map((column) => ({
      title: column.label,
      key: `consent_${column.key}`,
      width: 110,
      align: 'center' as const,
      render: (_: unknown, row: StopoverPendencyRowType) => (
        <ConsentSignalIcon
          status={row.consents[column.key]}
          tooltip={`${column.label}: ${consentStatusLabel(
            row.consents[column.key]
          )}`}
        />
      ),
    })),
    {
      title: 'Escala PMIS',
      dataIndex: 'code',
      key: 'code',
      width: 150,
    },
    {
      title: 'Agência',
      dataIndex: 'shipping_agency_name',
      key: 'shipping_agency_name',
      width: 200,
      render: text,
    },
    {
      title: 'Anuências',
      key: 'document_status',
      width: 100,
      align: 'center',
      render: (_, row) =>
        row.document_status ? (
          <ConsentSignalIcon
            status={row.document_status}
            tooltip={consentStatusLabel(row.document_status)}
          />
        ) : (
          '-'
        ),
    },
    {
      title: 'Anuências atualizadas em',
      dataIndex: 'document_status_synced_at',
      key: 'document_status_synced_at',
      width: 160,
      render: (value) =>
        value ? (
          <span title={moment(value).fromNow()}>
            {createDateStringPtBr(value)}
          </span>
        ) : (
          '-'
        ),
    },
  ];
}
